import EventNoteIcon from '@mui/icons-material/EventNote';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fighter } from '../../../../../corelogic/domain/models/fighter.model';
import { FighterWithDescription } from '../../../../../corelogic/domain/models/hub.model';
import { checkSelectedSeasonFights } from '../../../../../corelogic/domain/utils/checkSelectedSeasonFights.utils';
import { parseSafeDate } from '../../../../../corelogic/domain/utils/date.utils';
import { getUniqueUpcomingEvents } from '../../../../../corelogic/domain/utils/event.utils';
import { calculateTotalPointsForFighter } from '../../../../../corelogic/domain/utils/fighter.utils';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useFighterState } from '../../hooks/useFighterState';
import { useFightState } from '../../hooks/useFightState';
import { useSeasonFightsState } from '../../hooks/useSeasonFightsState';
import { useSeasonState } from '../../hooks/useSeasonState';
import { useUserSeasonStatsState } from '../../hooks/useUserSeasonStatsState';
import { SeasonFightsService } from '../../services/SeasonFightsService';
import { UserSeasonService } from '../../services/UserSeasonService';
import { UserSeasonStatsService } from '../../services/UserSeasonStatsService';
import SeasonSelector from '../SeasonSelector/SeasonSelector';
import FighterDetailsModal from '../Shared/FighterDetailsModal/FighterDetailsModal';
import ReusableAlert from '../Shared/ReusableAlert/ReusableAlert';
import { fightersToWatch } from './Components/Contents/fightersToWatch';
import FighterToWatchList from './Components/FighterToWatch/FighterToWatchList';
import PopularFighterSection from './Components/PopularFighter/PopularFighterSection';
import Guide from './Components/PublicViewables/Guide';
import NewsHub from './Components/PublicViewables/NewsHub';
import RankedFighterSection from './Components/RankedFighter/RankedFighterSection';
import RankedUserSection from './Components/RankedUser/RankedUserSection';
import ScrollingText from './Components/ScrollingText';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

const headerStyle = {
  p: 2,
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const mainHeaderStyle = {
  ...headerStyle,
  bgcolor: '#8b0000',
  color: 'white',
};

const titleStyle = {
  fontFamily: '"Rubik Mono One", sans-serif',
};

const mainTitleStyle = {
  ...titleStyle,
  fontStyle: 'italic',
};

const paperStyle = {
  overflow: 'auto',
  height: { xs: 'auto', md: '600px' },
  minHeight: { xs: '400px', md: '600px' },
};

const Hub: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthentication();
  const { seasonId: seasonFightsId, fights: seasonFights } =
    useSeasonFightsState();

  const { seasons } = useSeasonState();

  const { fights: allFights, isLoading: isFightsLoading } = useFightState();

  const { fighters: allFighters } = useFighterState();

  const { usersTotalPoints, fighterFrequency } = useUserSeasonStatsState();

  const selectedSeasonId = seasonFightsId;

  const [snackbarText, setSnackbarText] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [selectedFighter, setSelectedFighter] = useState<Fighter | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleSeasonChange = (seasonId: string) => {
    if (isFightsLoading) {
      console.log('Fights still loading, proceeding with season change...');
      SeasonFightsService.retrieveSeasonFights(seasonId);
      UserSeasonService.retrieveUserSeason(seasonId);
      return;
    }

    const fightsInSeason = checkSelectedSeasonFights(
      seasons,
      allFights,
      seasonId,
    );

    if (!fightsInSeason) {
      setSnackbarText(
        `No Events scheduled for this season. Please select another season.`,
      );
      setOpenSnackbar(true);
      return;
    }

    SeasonFightsService.retrieveSeasonFights(seasonId);
    UserSeasonService.retrieveUserSeason(seasonId);
  };

  useEffect(() => {
    if (seasonFightsId) {
      UserSeasonStatsService.retrieveFighterFrequencyBySeason(seasonFightsId);
      UserSeasonStatsService.retrieveUsersTotalPointsBySeason(seasonFightsId);
    }
    console.log('Hub: usersTotalPoints', usersTotalPoints);
    console.log('Hub: fighterFrequency', fighterFrequency);
  }, [seasonFightsId]);

  const topFighters = useMemo(() => {
    // Create an array to store all fighter appearances with their points and date
    const fighterAppearances: Array<{
      fighter: Fighter;
      points: number;
      date: Date;
    }> = [];

    // Iterate through season fights and add each fighter appearance
    seasonFights.forEach((seasonFight) => {
      const fightDetails = allFights.find(
        (fight) => fight.id === seasonFight.fight_id,
      );
      if (fightDetails) {
        // Add fighter 1 appearance
        const fighter1 = allFighters.find(
          (f) => f.id === seasonFight.fighter_1_id,
        );
        if (fighter1) {
          fighterAppearances.push({
            fighter: fighter1,
            points: fightDetails.fighter_1_points || 0,
            date: parseSafeDate(seasonFight.date),
          });
        }

        // Add fighter 2 appearance
        const fighter2 = allFighters.find(
          (f) => f.id === seasonFight.fighter_2_id,
        );
        if (fighter2) {
          fighterAppearances.push({
            fighter: fighter2,
            points: fightDetails.fighter_2_points || 0,
            date: parseSafeDate(seasonFight.date),
          });
        }
      }
    });

    // Sort by points and take top 10
    return fighterAppearances.sort((a, b) => b.points - a.points).slice(0, 10);
  }, [seasonFights, allFights, allFighters]);

  // Use the allFighters array to find specific fighters to watch from our data
  const watchListFighters = fightersToWatch
    .map(({ name, description, fightId }) => {
      const fighter = allFighters.find(
        (f) => `${f.firstname} ${f.lastname}` === name,
      );
      return fighter ? { fighter, description, fightId } : null;
    })
    .filter((item): item is FighterWithDescription => item !== null);

  const mostPopularFighters = useMemo(() => {
    // Create an array to store the most popular fighters
    const popularFighters: Array<{
      fighter: Fighter;
      points: number;
      selectedAmount: number;
    }> = [];

    if (!fighterFrequency) {
      return popularFighters;
    }

    fighterFrequency.forEach(({ fighterId, frequency }) => {
      const fighter = allFighters.find((f) => f.id === fighterId);
      if (fighter) {
        const totalPoints = calculateTotalPointsForFighter(
          fighterId,
          seasonFights,
          allFights,
        );
        popularFighters.push({
          fighter,
          points: totalPoints,
          selectedAmount: frequency,
        });
      }
    });
    console.log('Hub: mostPopularFighters', popularFighters);
    return popularFighters;
  }, [fighterFrequency, seasonFights, allFights, allFighters]);

  const handleFighterClick = (fighter: Fighter) => {
    setSelectedFighter(fighter);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundImage:
          'linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9)), url(/viewer_octagon.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        overflow: 'auto',
        pb: { xs: 4, md: 0 },
      }}
    >
      {/* Content Section */}
      <Box sx={{ flex: 1, p: { xs: 1, sm: 2, md: 3 }, overflow: 'visible' }}>
        <motion.div variants={container} initial="hidden" animate="show">
          {/* Season Events List */}
          <motion.div variants={item}>
            <Box
              sx={{
                ...mainHeaderStyle,
                bgcolor: 'transparent',
                borderBottom: '2px solid white',
                mb: 4,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  ...mainTitleStyle,
                  fontSize: 'clamp(1.3rem, 3.5vw, 2.2rem)',
                }}
              >
                Fantasy MMA Hub
              </Typography>
            </Box>
            <Paper
              sx={{
                ...paperStyle,
                mb: 3,
                bgcolor: 'transparent',
              }}
            >
              <Box sx={{ padding: 2, height: '100%' }}>
                <Grid container spacing={3} sx={{ height: '100%' }}>
                  {/* News Hub Section */}
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                      width: { xs: '100%', md: '50%' },
                      px: { xs: 0, md: 2 },
                      height: { xs: 'auto', md: '100%' },
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          color: 'white',
                          ...titleStyle,
                          fontSize: 'clamp(0.8rem, 3vw, 1.5rem)',
                          mb: 2,
                          textAlign: { xs: 'center', md: 'left' },
                        }}
                      >
                        Latest Announcements
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'grey.800',
                          borderRadius: '8px',
                          height: { xs: 'auto', md: 'calc(100% - 10px)' },
                          overflow: 'auto',
                          width: '100%',
                        }}
                      >
                        <NewsHub />
                      </Box>
                    </Box>
                  </Grid>

                  {/* Guide Section */}
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                      width: { xs: '100%', md: '50%' },
                      px: { xs: 0, md: 2 },
                      height: { xs: 'auto', md: '100%' },
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          color: 'white',
                          ...titleStyle,
                          fontSize: 'clamp(0.8rem, 3vw, 1.5rem)',
                          mb: 2,
                          textAlign: { xs: 'center', md: 'left' },
                        }}
                      >
                        How to Play Fantasy MMA
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'grey.800',
                          borderRadius: '8px',
                          height: { xs: 'auto', md: 'calc(100% - 10px)' },
                          overflow: 'auto',
                          width: '100%',
                        }}
                      >
                        <Guide />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </motion.div>

          {/* News Section Separator */}
          <Box sx={{ mt: 4, mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', sm: 'space-between' },
                p: 2,
                bgcolor: 'transparent',
                borderBottom: '2px solid white',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 },
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  ...mainTitleStyle,
                  color: 'white',
                  textAlign: { xs: 'center', sm: 'left' },
                  fontSize: 'clamp(1.3rem, 3.5vw, 2.2rem)',
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  width: { xs: '100%', sm: 'auto' },
                  mb: { xs: 1, sm: 0 },
                }}
              >
                Fantasy MMA - Breakdown
              </Typography>
              {isAuthenticated && (
                <Box sx={{ width: 'auto', flexShrink: 0 }}>
                  <SeasonSelector
                    selectedSeasonId={selectedSeasonId}
                    onSeasonChange={handleSeasonChange}
                  />
                </Box>
              )}
            </Box>

            {!isAuthenticated ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: { xs: '300px', md: '300px' },
                  bgcolor: 'rgba(0, 0, 0, 0.4)',
                  borderRadius: '8px',
                  mt: 2,
                  p: 4,
                }}
              >
                <Box
                  onClick={() => navigate('/login')}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '20px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.01)',
                      '& .MuiSvgIcon-root': {
                        transform: 'scale(1.1)',
                        color: '#ff0000',
                      },
                    },
                  }}
                >
                  <LockIcon
                    sx={{
                      fontSize: 'clamp(3rem, 6vw, 5rem)',
                      color: '#8b0000',
                      mb: 3,
                      transition: 'all 0.2s ease-in-out',
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Bebas Neue',
                        fontSize: 'clamp(1.2rem, 3vw, 2.2rem)',
                        color: 'white',
                        textAlign: 'center',
                        letterSpacing: '1px',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                      }}
                    >
                      LOGIN OR SIGN UP
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Bebas Neue',
                        fontSize: 'clamp(1rem, 2.5vw, 1.8rem)',
                        color: 'white',
                        textAlign: 'center',
                        letterSpacing: '1px',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                        opacity: 0.8,
                      }}
                    >
                      TO SEE MORE
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    borderRadius: '8px',
                    p: 1,
                    mt: 2,
                    mb: 3,
                    // borderTop: '1px solid white',
                    // borderBottom: '1px solid white',
                    pl: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: 'grey.800',
                      borderRadius: '8px',
                      p: 1,
                    }}
                  >
                    <EventNoteIcon sx={{ color: '#cc0000', ml: 2 }} />
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'white',
                        fontFamily: 'Bebas Neue',
                        fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
                        ml: 2,
                      }}
                    >
                      Upcoming Events:
                    </Typography>
                  </Box>
                  <Divider
                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)', mx: 2 }}
                    orientation="vertical"
                    flexItem
                  />
                  <ScrollingText
                    events={getUniqueUpcomingEvents(seasonFights)}
                  />
                </Box>

                <Grid
                  container
                  spacing={3}
                  sx={{ height: { xs: 'auto', md: '100%' } }}
                >
                  {/* Top Fighters Section */}
                  <Grid item xs={12} md={6}>
                    <RankedFighterSection
                      title="Top 10 Scoring Fighters (by fight)"
                      icon={<SportsMmaIcon sx={{ color: 'white' }} />}
                      items={topFighters}
                      onFighterClick={handleFighterClick}
                    />
                  </Grid>

                  {/* Top Performing Users Section */}
                  <Grid item xs={12} md={6}>
                    <RankedUserSection
                      title="Top Performing Users (for this season)"
                      icon={<GroupIcon sx={{ color: 'white' }} />}
                      items={usersTotalPoints || []}
                    />
                  </Grid>

                  {/* Hottest Fighters Section - PLACEHOLDER WILL REPLACE WITH USER PICKED WHEN ENDPOINT IMPLEMENTED */}
                  <Grid item xs={12} md={6}>
                    <motion.div variants={item}>
                      <PopularFighterSection
                        title="Top 10 Picked Fighters (for this season)"
                        icon={<SportsMmaIcon sx={{ color: 'white' }} />}
                        items={mostPopularFighters}
                        onFighterClick={handleFighterClick}
                      />
                    </motion.div>
                  </Grid>

                  {/* Fighters to Watch Section */}
                  <Grid item xs={12} md={6}>
                    <motion.div variants={item}>
                      <FighterToWatchList
                        fighters={watchListFighters}
                        allFights={allFights}
                        onFighterClick={handleFighterClick}
                      />
                    </motion.div>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </motion.div>
      </Box>
      <ReusableAlert
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        severity="warning"
        text={snackbarText}
      />
      <FighterDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        fighter={selectedFighter}
        fights={allFights}
      />
    </Box>
  );
};

export default Hub;
